import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  api, SALE, ESAMI, ESAMI_EXTRA, esamiAttivi, EVENTO_LABEL, STATO_STYLE,
  saleRichieste, openConsenso, openScheda, formatDateTime, validaCodiceFiscale,
  tempiVisita, minuti,
} from "@/lib/clinic";
import { Loader2, Printer, Save, FileText, CheckCircle2, AlertTriangle, Mail } from "lucide-react";

const CAMPI = [
  ["nome", "Nome"],
  ["cognome", "Cognome"],
  ["azienda", "Azienda"],
  ["mansione", "Mansione"],
  ["tipo_visita", "Tipo visita"],
  ["data_nascita", "Data di nascita"],
  ["luogo", "Luogo"],
  ["codice_fiscale", "Codice fiscale"],
  ["email", "E-mail"],
  ["nazionalita", "Nazionalità"],
  ["referente", "Referente aziendale"],
  ["contatto", "Contatto"],
];

const SALVABILI = [...CAMPI.map(([k]) => k), "data", "orario", "note", "visita", "ambulatorio", "esami"];

const istantanea = (p) => JSON.stringify(SALVABILI.map((k) => p?.[k] ?? ""));

export const PatientDetailDialog = ({ patient, open, onOpenChange }) => {
  const [form, setForm] = useState({});
  const [tocchi, setTocchi] = useState([]);
  const [esterno, setEsterno] = useState(false);
  const [confermaUscita, setConfermaUscita] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inviando, setInviando] = useState(false);
  const idRef = useRef(null);
  const serverRef = useRef(null);

  // La scheda si ricarica solo all'apertura o cambiando paziente: le azioni delle altre
  // postazioni aggiornano soltanto i campi non ancora modificati qui.
  useEffect(() => {
    if (!open || !patient) return;
    if (idRef.current !== patient.id) {
      idRef.current = patient.id;
      serverRef.current = istantanea(patient);
      setForm({ ...patient, esami: { ...(patient.esami || {}) } });
      setTocchi([]);
      setEsterno(false);
      return;
    }
    const snap = istantanea(patient);
    if (snap === serverRef.current) return;
    serverRef.current = snap;
    setEsterno(true);
    setForm((f) => {
      const next = { ...patient, esami: { ...(patient.esami || {}) } };
      tocchi.forEach((k) => {
        next[k] = f[k];
      });
      return next;
    });
  }, [open, patient, tocchi]);

  useEffect(() => {
    if (!open) idRef.current = null;
  }, [open]);

  if (!patient) return null;

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setTocchi((t) => (t.includes(k) ? t : [...t, k]));
  };

  const toggleEsame = (k) => {
    setTocchi((t) => (t.includes("esami") ? t : [...t, "esami"]));
    setForm((f) => {
      const esami = { ...(f.esami || {}) };
      if (esami[k]) delete esami[k];
      else esami[k] = "X";
      return { ...f, esami };
    });
  };

  const chiudi = (aperto) => {
    if (aperto) return;
    if (tocchi.length) {
      setConfermaUscita(true);
      return;
    }
    onOpenChange(false);
  };

  const esciSenzaSalvare = () => {
    setConfermaUscita(false);
    setTocchi([]);
    onOpenChange(false);
  };

  const salva = async () => {
    if (!tocchi.length) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      // Si inviano solo i campi davvero modificati: nessuna sovrascrittura del lavoro altrui.
      const payload = {};
      tocchi.forEach((k) => {
        if (k === "esami") payload.esami = form.esami || {};
        else if (k === "visita" || k === "ambulatorio") payload[k] = !!form[k];
        else payload[k] = form[k] ?? "";
      });
      await api.put(`/patients/${patient.id}`, payload);
      toast.success("Dati paziente aggiornati");
      setTocchi([]);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile salvare");
    } finally {
      setSaving(false);
    }
  };

  const inviaPromemoria = async () => {
    setInviando(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/email-promemoria`);
      toast.success(`Promemoria inviato a ${data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile inviare il promemoria");
    } finally {
      setInviando(false);
    }
  };

  const sta = STATO_STYLE[patient.stato] || STATO_STYLE.da_accettare;

  const cfErrore = validaCodiceFiscale(form.codice_fiscale);

  return (
    <>
    <Dialog open={open} onOpenChange={chiudi}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="patient-detail-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-ticket text-2xl font-bold text-slate-900" data-testid="detail-codice">
              {patient.codice || "—"}
            </span>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {[patient.nome, patient.cognome].filter(Boolean).join(" ")}
            </DialogTitle>
            <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${sta.cls}`}>{sta.label}</span>
          </div>
          <DialogDescription className="text-sm text-slate-600">
            Scheda paziente modificabile: dati anagrafici, esami richiesti e attività registrate.
          </DialogDescription>
          {esterno && (
            <div
              data-testid="detail-external-update"
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              Scheda aggiornata da un'altra postazione: le modifiche che stai scrivendo sono state mantenute.
            </div>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CAMPI.map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500">{label}</Label>
                  <Input
                    value={form[key] || ""}
                    onChange={(e) => set(key)(key === "codice_fiscale" ? e.target.value.toUpperCase() : e.target.value)}
                    data-testid={`detail-input-${key}`}
                    className={key === "codice_fiscale" && cfErrore ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                  />
                  {key === "codice_fiscale" && cfErrore && (
                    <div className="flex items-start gap-1.5 text-[11px] text-amber-700" data-testid="detail-cf-warning">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {cfErrore}
                    </div>
                  )}
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">Data visita</Label>
                <Input type="date" value={form.data || ""} onChange={(e) => set("data")(e.target.value)} data-testid="detail-input-data" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">Orario (opzionale)</Label>
                <Input type="time" value={form.orario || ""} onChange={(e) => set("orario")(e.target.value)} data-testid="detail-input-orario" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500">Sale previste</Label>
              <div className="flex flex-wrap gap-3">
                {["levi", "jenner"].map((r) => (
                  <label
                    key={r}
                    className={`flex items-center gap-2.5 rounded-xl border px-4 py-2.5 cursor-pointer ${
                      (r === "levi" ? form.visita : form.ambulatorio) ? "border-sky-400 bg-sky-50/70" : "border-slate-200"
                    }`}
                  >
                    <Checkbox
                      checked={r === "levi" ? !!form.visita : !!form.ambulatorio}
                      onCheckedChange={(v) => set(r === "levi" ? "visita" : "ambulatorio")(!!v)}
                      data-testid={`detail-sala-${r}`}
                    />
                    <span className="text-sm font-medium">{SALE[r].label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500">Esami richiesti</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries({ ...ESAMI, ...ESAMI_EXTRA }).map(([k, label]) => {
                  const active = !!form.esami?.[k];
                  return (
                    <button
                      type="button"
                      key={k}
                      onClick={() => toggleEsame(k)}
                      data-testid={`detail-esame-${k}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                        active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      {k} · {label}
                    </button>
                  );
                })}
              </div>
              {esamiAttivi(patient).some((e) => e.valore && e.valore !== "X") && (
                <div className="text-xs text-slate-500 space-y-0.5 pt-1">
                  {esamiAttivi(patient)
                    .filter((e) => e.valore && e.valore !== "X")
                    .map((e) => (
                      <div key={e.key}>
                        <span className="font-semibold">{e.label}:</span> {e.valore}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500">Note</Label>
              <Textarea rows={2} value={form.note || ""} onChange={(e) => set("note")(e.target.value)} data-testid="detail-input-note" />
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={salva} disabled={saving} className="rounded-full px-6" data-testid="detail-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Salva modifiche
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => openScheda(patient)} data-testid="detail-print-scheda-btn">
                <FileText className="w-4 h-4 mr-2" /> Scheda paziente
              </Button>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => openConsenso(patient)}
                data-testid="detail-print-consenso-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Consenso
              </Button>
              <Button
                variant="outline"
                className="rounded-full"
                disabled={inviando || !patient.email}
                title={patient.email ? `Invia a ${patient.email}` : "Nessuna e-mail in scheda"}
                onClick={inviaPromemoria}
                data-testid="detail-send-email-btn"
              >
                {inviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Promemoria e-mail
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Riepilogo</div>
              <Riga label="Arrivo confermato" value={patient.orario_arrivo || "—"} />
              <Riga label="Sale" value={saleRichieste(patient).map((r) => SALE[r].short).join(" + ") || "—"} />
              {saleRichieste(patient).map((r) => (
                <Riga key={r} label={SALE[r].short} value={patient.sale?.[r] ? patient.sale[r].replace("_", " ") : "—"} />
              ))}
              <Riga label="Data" value={patient.data} />
            </div>

            <div className="rounded-xl border border-slate-200 bg-sky-50/60 p-4 space-y-2 text-sm" data-testid="detail-tempi-section">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Tempi di visita</div>
              {(() => {
                const t = tempiVisita(patient);
                return (
                  <>
                    <Riga label="Arrivo" value={t.arrivo || "—"} />
                    {saleRichieste(patient).map((r) => (
                      <div key={r} className="rounded-lg bg-white/80 border border-slate-200 px-3 py-2 space-y-1">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                          {SALE[r].short}
                        </div>
                        <div className="flex items-baseline justify-between gap-2 text-xs text-slate-500">
                          <span>Inizio</span>
                          <span className="font-ticket text-sm text-slate-900" data-testid={`detail-inizio-${r}`}>
                            {t.sale[r]?.inizio || "—"}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2 text-xs text-slate-500">
                          <span>Fine</span>
                          <span className="font-ticket text-sm text-slate-900" data-testid={`detail-fine-${r}`}>
                            {t.sale[r]?.fine || "—"}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2 text-xs text-slate-500">
                          <span>Durata</span>
                          <span className="font-semibold text-sm text-sky-700" data-testid={`detail-durata-${r}`}>
                            {minuti(t.sale[r]?.durata)}
                          </span>
                        </div>
                      </div>
                    ))}
                    <Riga label="Attesa prima della chiamata" value={minuti(t.attesa)} plain />
                    <Riga label="Tempo in visita" value={minuti(t.visita)} plain />
                    <Riga label="Totale in studio" value={minuti(t.totale)} plain />
                  </>
                );
              })()}
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Attività registrate</div>
              <ul className="space-y-2.5" data-testid="detail-events-list">
                {(patient.eventi || []).slice().reverse().map((ev, i) => (
                  <li key={i} className="flex gap-3 text-sm" data-testid="detail-event-item">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-medium text-slate-800">
                        {EVENTO_LABEL[ev.tipo] || ev.tipo}
                        {ev.sala ? ` — ${SALE[ev.sala]?.short}` : ""}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatDateTime(ev.at)}
                        {ev.nota ? ` · ${ev.nota}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
                {(patient.eventi || []).length === 0 && (
                  <li className="text-sm text-slate-400">Nessuna attività registrata</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={confermaUscita} onOpenChange={setConfermaUscita}>
      <AlertDialogContent data-testid="detail-unsaved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Modifiche non salvate</AlertDialogTitle>
          <AlertDialogDescription>
            Hai modificato {tocchi.length === 1 ? "un campo" : `${tocchi.length} campi`} della scheda senza salvare.
            Vuoi uscire e perdere le modifiche?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="detail-unsaved-stay-btn">Continua a modificare</AlertDialogCancel>
          <AlertDialogAction onClick={esciSenzaSalvare} data-testid="detail-unsaved-leave-btn" className="bg-rose-600 hover:bg-rose-700">
            Esci senza salvare
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

const Riga = ({ label, value, plain }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`font-medium text-slate-900 text-sm ${plain ? "" : "capitalize"}`}>{value || "—"}</span>
  </div>
);
